/**
 * Tenant Configuration and Detection
 *
 * This module provides tenant-specific configuration for multi-country support.
 * Tenants are detected based on URL paths (e.g., /au for Australia).
 */

import { Project } from './project'

/**
 * Canonical tenant identifier. Numeric values match the API tenant IDs sent in
 * the `Tenant-Id-Authentication` header, so the enum doubles as that ID.
 */
export enum Tenant {
  UK = 1,
  AU = 2,
}

export type TenantId = Tenant

const TENANT_SLUGS: Record<Tenant, string> = {
  [Tenant.UK]: 'hoxton-mix-uk',
  [Tenant.AU]: 'hoxton-mix-au',
}

/** Map a Tenant enum to its tenant slug. */
export function tenantToSlug(tenant: Tenant): string {
  return TENANT_SLUGS[tenant] || TENANT_SLUGS[Tenant.UK]
}

export type PaymentGateway = 'CARD' | 'PAYPAL' | 'GO_CARDLESS'

export interface TenantConfig {
  id: TenantId
  name: string
  slug: string
  vat_rate: number
  region: number
  default_location: number
  country: string // ISO 3166-1 alpha-2 country code (e.g., 'GB', 'AU')
  currency?: string // Currency code (e.g., 'GBP', 'AUD')
  locale?: string // Locale for formatting (e.g., 'en-GB', 'en-AU')
  paymentGateways: PaymentGateway[]
}

/**
 * Map region to country, currency and locale
 * TODO: This should eventually come from the API
 */
function getRegionConfig(region: number): {
  country: string
  currency: string
  locale: string
  paymentGateways: PaymentGateway[]
} {
  switch (region) {
    case Tenant.UK:
      return {
        country: 'GB',
        currency: 'GBP',
        locale: 'en-GB',
        paymentGateways: ['CARD', 'PAYPAL', 'GO_CARDLESS'],
      }
    case Tenant.AU: // Australia
      return {
        country: 'AU',
        currency: 'AUD',
        locale: 'en-AU',
        paymentGateways: ['CARD'],
      }
    default:
      return {
        country: 'GB',
        currency: 'GBP',
        locale: 'en-GB',
        paymentGateways: ['CARD', 'PAYPAL', 'GO_CARDLESS'],
      }
  }
}

// Lazy-loaded tenant data from compiled bundle
let compiledTenantData: Record<string, any> | null = null

/**
 * Get compiled tenant data from build-time generation
 */
function getCompiledTenantData(): Record<string, any> {
  if (!compiledTenantData) {
    // This import is resolved at build time and inlined
    const { STATIC_TENANT_DATA } = require('./utils/planPrice')
    compiledTenantData = STATIC_TENANT_DATA
  }
  return compiledTenantData!
}

/**
 * Get tenant configuration from compiled data
 * Uses data fetched from API at build time
 */
function getTenantConfigFromSlug(slug: string): TenantConfig {
  const tenantData = getCompiledTenantData()
  const data = tenantData[slug] || tenantData['hoxton-mix-uk'] // Default to UK

  const regionConfig = getRegionConfig(data.region)

  return {
    // The tenants API is adding an ISO country code; fall back until it ships
    country: data.country || regionConfig.country,
    currency: regionConfig.currency,
    default_location: data.default_location,
    id: data.id,
    locale: regionConfig.locale,
    name: data.name,
    paymentGateways: regionConfig.paymentGateways,
    region: data.region,
    slug: data.slug,
    vat_rate: data.vat_rate,
  }
}

/**
 * Get tenant slug based on URL path or tenant identifier
 * @param path - The URL path (e.g., '/au/checkout'), tenant identifier (e.g., 'au'), or full path
 * @returns Tenant slug
 */
function getTenantSlugFromPath(path: string): string {
  const normalizedPath = path.toLowerCase().trim()

  // Check if it's already a tenant slug
  if (normalizedPath === 'hoxton-mix-au') {
    return 'hoxton-mix-au'
  }
  if (normalizedPath === 'hoxton-mix-uk') {
    return 'hoxton-mix-uk'
  }

  // Check if it's just "au" or the /au segment (not e.g. /audit)
  if (normalizedPath === 'au' || /^\/au(\/|\?|#|$)/.test(normalizedPath)) {
    return 'hoxton-mix-au'
  }

  // Check if it's just "uk" or starts with "/uk"
  if (normalizedPath === 'uk' || normalizedPath.startsWith('/uk')) {
    return 'hoxton-mix-uk'
  }

  // Default to UK tenant
  return 'hoxton-mix-uk'
}

/**
 * Get tenant based on URL path or tenant identifier
 * @param path - The URL path (e.g., '/au/checkout'), tenant identifier (e.g., 'au'), or '/checkout'
 * @returns Tenant configuration
 */
export function getTenantFromPath(path: string): TenantConfig {
  const slug = getTenantSlugFromPath(path)
  return getTenantConfigFromSlug(slug)
}

/**
 * Get tenant from router (for use in components)
 * @param asPath - The router.asPath value from next/router
 * @returns Tenant configuration
 */
export function getTenantFromRouter(asPath: string): TenantConfig {
  return getTenantFromPath(asPath)
}

/**
 * Get current tenant on the client side
 * @returns Tenant configuration
 */
export function getCurrentTenant(): TenantConfig {
  if (typeof window === 'undefined') {
    // Server-side: default to UK
    return getTenantConfigFromSlug('hoxton-mix-uk')
  }

  const slug = getTenantSlugFromPath(window.location.pathname)
  return getTenantConfigFromSlug(slug)
}

/**
 * Get tenant config (synchronous)
 * @param pathOrTenant - Optional Tenant enum, URL path, or tenant slug override.
 *   When omitted, uses the current location.
 * @returns Tenant configuration
 */
export function getTenantConfig(pathOrTenant?: string | Tenant): TenantConfig {
  const slug =
    typeof pathOrTenant === 'number'
      ? tenantToSlug(pathOrTenant)
      : pathOrTenant
      ? getTenantSlugFromPath(pathOrTenant)
      : typeof window !== 'undefined'
      ? getTenantSlugFromPath(window.location.pathname)
      : 'hoxton-mix-uk'

  return getTenantConfigFromSlug(slug)
}

/**
 * Thrown from routeChangeStart in _app to cancel a client-side navigation
 * between tenants (which then happens as a full page load). Next 13 emits
 * routeChangeStart outside its cancellation try/catch, so this surfaces as an
 * unhandled rejection; Sentry ignores it by this message.
 */
export const CROSS_TENANT_NAVIGATION_ABORT =
  'Cross-tenant navigation: full page load'

/**
 * Currency code for the current (or given) tenant, e.g. 'GBP' or 'AUD'.
 * Used for analytics events so AU amounts aren't reported as GBP.
 */
export function getTenantCurrency(pathOrTenant?: string | Tenant): string {
  return getTenantConfig(pathOrTenant).currency ?? 'GBP'
}

/**
 * Get tenant ID for API requests
 * @param path - Optional path override, otherwise uses current location
 * @returns Tenant ID
 */
export function getTenantId(path?: string): TenantId {
  return getTenantConfig(path).id
}

/**
 * Storage key for the last completed order. UK keeps 'order' for backwards
 * compatibility; other tenants use order_<tenant id>, e.g. order_2
 */
export function getOrderStorageKey(tenant: Tenant = getTenantId()): string {
  return tenant === Tenant.UK ? 'order' : `order_${tenant}`
}

/**
 * Storage key for the in-progress checkout form, so UK and AU drafts
 * never restore into each other's checkout
 */
export function getCheckoutStorageKey(path?: string): string {
  const tenant = getTenantId(path)
  // UK keeps the original key; other tenants get a suffix, e.g. checkout-au
  return tenant === Tenant.UK
    ? 'checkout'
    : `checkout-${Tenant[tenant].toLowerCase()}`
}

/**
 * Get API headers with tenant authentication
 * @param path - Optional path override for tenant detection
 * @returns Headers object with Tenant-Id-Authentication
 */
export function getTenantHeaders(path?: string): Record<string, string> {
  const tenantId = getTenantId(path)
  return {
    'Content-Type': 'application/json',
    'Tenant-Id-Authentication': String(tenantId),
  }
}

/**
 * Check if current context is Australian tenant (synchronous)
 * @param path - Optional path override
 * @returns true if Australian tenant
 */
export function isAustralianTenant(path?: string): boolean {
  const tenantPath =
    path || (typeof window !== 'undefined' ? window.location.pathname : '')
  return getTenantSlugFromPath(tenantPath) === 'hoxton-mix-au'
}

/**
 * Check if current context is UK tenant (synchronous)
 * @param path - Optional path override
 * @returns true if UK tenant
 */
export function isUKTenant(path?: string): boolean {
  const tenantPath =
    path || (typeof window !== 'undefined' ? window.location.pathname : '')
  return getTenantSlugFromPath(tenantPath) === 'hoxton-mix-uk'
}

/**
 * React hook to get current tenant configuration
 * @returns Tenant configuration
 */
export function useTenantConfig(): TenantConfig {
  // Simple synchronous hook - no async needed
  return getTenantConfig()
}
